import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { adminAPI, crmAPI } from '../services/api';

function buildTemplateOptions(rawList) {
  const list = Array.isArray(rawList) ? rawList : [];
  return list
    .map((row) => {
      const id = String(row?.id || '').trim();
      if (!id) return null;
      const isCarousel = Boolean(
        row?.isCarousel ||
        row?.is_carousel ||
        String(row?.headerFormat || row?.header_format || '').toUpperCase() === 'CAROUSEL' ||
        (Array.isArray(row?.carouselCards) && row.carouselCards.length > 0) ||
        (Array.isArray(row?.carousel_cards) && row.carousel_cards.length > 0)
      );
      const carouselCards = Array.isArray(row?.carouselCards)
        ? row.carouselCards
        : (Array.isArray(row?.carousel_cards) ? row.carousel_cards : []);
      const carouselCardCount = row?.carouselCardCount || row?.carousel_card_count || carouselCards.length || 0;

      return {
        id,
        name: String(row?.name || id).trim() || id,
        language: String(row?.language || 'en_US').trim() || 'en_US',
        body: String(row?.body || '').trim(),
        mediaType: isCarousel ? 'carousel' : (String(row?.mediaType || row?.media_type || '').trim().toLowerCase() || null),
        headerFormat: isCarousel ? 'CAROUSEL' : (String(row?.headerFormat || row?.header_format || '').trim().toUpperCase() || null),
        sampleMediaUrl: String(row?.sampleMediaUrl || row?.sample_media_url || '').trim() || null,
        requiresMedia: Boolean(row?.requiresMedia || row?.requires_media || isCarousel),
        requiresVariables: Boolean(row?.requiresVariables || row?.requires_variables),
        isCarousel,
        carouselCards,
        carouselCardCount
      };
    })
    .filter(Boolean);
}

export default function LeadRequestWhatsAppDialog({ open, lead, onClose, onSent }) {
  const [templateId, setTemplateId] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const {
    data: waplifyTemplatesRes,
    isLoading: waplifyTemplatesLoading,
    error: waplifyTemplatesError
  } = useQuery(['waplify-templates'], () => adminAPI.getWaplifyTemplates(), {
    enabled: open,
    retry: false,
    staleTime: 5 * 60 * 1000
  });

  const templateOptions = useMemo(
    () => buildTemplateOptions(waplifyTemplatesRes?.data?.data),
    [waplifyTemplatesRes]
  );

  useEffect(() => {
    if (!open) {
      setTemplateId('');
      setSending(false);
      setError('');
    }
  }, [open]);

  const selectedTemplate = templateOptions.find((row) => row.id === templateId) || null;

  const handleSend = async () => {
    if (!lead?.phone) {
      setError('Phone number is missing.');
      return;
    }
    if (!selectedTemplate) {
      setError('Select a trek template.');
      return;
    }
    if (selectedTemplate.requiresMedia && !selectedTemplate.isCarousel && !lead?.trekId) {
      setError('This template needs a trek brochure PDF. Pick a lead linked to a trek, or choose a text-only template.');
      return;
    }
    setSending(true);
    setError('');
    try {
      const res = await crmAPI.sendLeadWhatsAppTemplate({
        phone: lead.phone,
        recipientName: lead.name || undefined,
        trekId: lead.trekId || undefined,
        templateId: selectedTemplate.id,
        templateName: selectedTemplate.name,
        templateLanguage: selectedTemplate.language,
        templateBody: selectedTemplate.body || undefined,
        templateMediaType: selectedTemplate.mediaType || undefined,
        templateHeaderFormat: selectedTemplate.headerFormat || undefined,
        templateSampleMediaUrl: selectedTemplate.sampleMediaUrl || undefined,
        templateRequiresMedia: selectedTemplate.requiresMedia,
        templateRequiresVariables: selectedTemplate.requiresVariables,
        isCarousel: selectedTemplate.isCarousel,
        carouselCards: selectedTemplate.carouselCards,
        carouselData: selectedTemplate.carouselCards?.map((c) => ({ mediaUrl: c.media_url || c.mediaUrl || '' }))
      });
      onSent?.(res?.data?.data);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not send WhatsApp template.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send WhatsApp</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Name</Typography>
            <Typography variant="body1">{lead?.name || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Phone</Typography>
            <Typography variant="body1">{lead?.phone || '—'}</Typography>
          </Box>
          {lead?.eventLabel ? (
            <Box>
              <Typography variant="body2" color="text.secondary">Trek / Event</Typography>
              <Typography variant="body1">{lead.eventLabel}</Typography>
            </Box>
          ) : null}
          <FormControl fullWidth size="small">
            <InputLabel id="lead-wa-template-label">Trek Template</InputLabel>
            <Select
              labelId="lead-wa-template-label"
              label="Trek Template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={waplifyTemplatesLoading || sending}
            >
              <MenuItem value="">
                <em>{waplifyTemplatesLoading ? 'Loading templates...' : 'No template selected'}</em>
              </MenuItem>
              {templateOptions.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {`${tpl.name} ${tpl.isCarousel ? `[CAROUSEL (${tpl.carouselCardCount || tpl.carouselCards?.length || ''})]` : `[${tpl.headerFormat || 'TEXT'}]`}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedTemplate?.requiresMedia && !selectedTemplate?.isCarousel && !lead?.trekId ? (
            <Typography variant="body2" color="warning.main">
              This template needs a brochure PDF from the linked trek. This lead has no trek — choose another template or edit the lead.
            </Typography>
          ) : null}
          {waplifyTemplatesError ? (
            <Typography variant="body2" color="error">
              {waplifyTemplatesError?.response?.data?.message || 'Unable to load Waplify templates.'}
            </Typography>
          ) : null}
          {error ? (
            <Typography variant="body2" color="error">{error}</Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || waplifyTemplatesLoading || !selectedTemplate || !lead?.phone}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
